import { useState } from 'react';
import { createMasterAdmin } from '../lib/createMasterAdmin';
import { Shield, CheckCircle, AlertCircle } from 'lucide-react';

export function AdminSetup() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleCreateAdmin = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await createMasterAdmin();

      if (response.success) {
        setResult({
          success: true,
          message: `Master admin created successfully! User ID: ${response.userId}`,
        });
      } else {
        setResult({
          success: false,
          message: response.error || 'Failed to create admin user',
        });
      }
    } catch (error: any) {
      setResult({
        success: false,
        message: error.message || 'An unexpected error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 border-2 border-gray-200">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Setup</h1>
            <p className="text-gray-600">Create master admin account</p>
          </div>
        </div>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Master Admin Credentials</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Email:</span>
              <span className="text-gray-900">ShaTera@313safebeds.com</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Password:</span>
              <span className="text-gray-900">123Safe</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Role:</span>
              <span className="text-gray-900">Admin</span>
            </div>
          </div>
        </div>

        {result && (
          <div
            className={`mb-6 p-4 rounded-xl border-2 ${
              result.success
                ? 'bg-green-50 border-green-300 text-green-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            <div className="flex items-start gap-3">
              {result.success ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="font-semibold">{result.message}</p>
            </div>
          </div>
        )}

        <button
          onClick={handleCreateAdmin}
          disabled={loading || (result?.success === true)}
          className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Creating Admin...
            </>
          ) : result?.success ? (
            <>
              <CheckCircle className="w-5 h-5" />
              Admin Created Successfully
            </>
          ) : (
            <>
              <Shield className="w-5 h-5" />
              Create Master Admin
            </>
          )}
        </button>

        {result?.success && (
          <div className="mt-6 text-center">
            <a
              href="/login"
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
            >
              Go to Login
            </a>
          </div>
        )}

        <div className="mt-6 pt-6 border-t-2 border-gray-200">
          <h3 className="font-bold text-gray-900 mb-2">Important Notes:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• This page should only be used once to create the master admin</li>
            <li>• The admin will have full access to all platform features</li>
            <li>• After creation, login with the credentials above</li>
            <li>• The admin dashboard will be available at /admin/dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
